import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ROUTES, ROUTES_BY_SERVICE_CODE } from '@/router/config';

import { EventSessionExpired, die, on } from 'src/eventBus';

import { clearSessionExpiredModalOpen, setSessionExpiredModalOpen } from './utils/flags';
import { clearSessionExpiredData, writeSessionExpiredData, SessionExpiredLoginState } from './utils/storage';

const SERVICE_PATH_PREFIXES = Object.values(ROUTES_BY_SERVICE_CODE).filter(
  (path) => typeof path === 'string' && path.length > 0 && path !== ROUTES.HOME,
);

const ACTION = {
  OPEN: 'OPEN',
  CLOSE_STAY: 'CLOSE_STAY',
  CLOSE_LOGIN: 'CLOSE_LOGIN',
} as const;

type State = Pick<Output, 'allowPath' | 'isModalOpen' | 'loginState'>;

type Action =
  | { type: typeof ACTION.OPEN; allowPath: string; loginState: State['loginState'] }
  | { type: typeof ACTION.CLOSE_STAY }
  | { type: typeof ACTION.CLOSE_LOGIN };

export interface Output {
  allowPath: Nullable<string>;
  isModalOpen: boolean;
  loginState: SessionExpiredLoginState;
  onStay: () => void;
  onLogin: () => void;
}

export const useSessionExpiredContext = (): Output => {
  const location = useLocation();
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(reducer, { allowPath: null, isModalOpen: false, loginState: {} });

  const currentPath = useMemo(() => `${location.pathname}${location.search}`, [location.pathname, location.search]);

  const clearSessionExpiredStorage = useCallback(() => {
    clearSessionExpiredData();
    clearSessionExpiredModalOpen();
  }, []);

  const handleSessionExpired = useCallback(
    (event: EventSessionExpired) => {
      if (state.isModalOpen) return;

      const expiredUrl = event?.url || currentPath;

      setSessionExpiredModalOpen();

      const locationState = (location.state || {}) as State['loginState'];
      const gercParams = location.pathname === ROUTES.GERC_REDIRECT ? new URLSearchParams(location.search) : null;
      const gercRequestIdFromQuery = Number(gercParams?.get('requestId'));
      const gercRequestIdFromState = locationState?.continueGercPayment ? Number(locationState?.request_id) : NaN;
      const gercRequestId = Number.isFinite(gercRequestIdFromState) ? gercRequestIdFromState : gercRequestIdFromQuery;
      const isGercRedirect = location.pathname === ROUTES.GERC_REDIRECT && Number.isFinite(gercRequestId);

      const servicePath = getMatchedServicePath(location.pathname);
      const redirectPath = servicePath ?? expiredUrl;

      const gercState = isGercRedirect
        ? {
            continueGercPayment: true,
            request_id: gercRequestId,
          }
        : {};

      const nextLoginState = {
        ...locationState,
        ...gercState,
        nextPage: redirectPath,
        isFromService: Boolean(servicePath) || isGercRedirect,
      } satisfies State['loginState'];

      dispatch({ type: ACTION.OPEN, allowPath: expiredUrl, loginState: nextLoginState });
      writeSessionExpiredData({ allowPath: expiredUrl, loginState: nextLoginState });
    },
    [currentPath, location.pathname, location.search, location.state, state.isModalOpen],
  );

  useEffect(() => {
    on('session_expired', handleSessionExpired);

    return (): void => {
      die('session_expired');
    };
  }, [handleSessionExpired]);

  const onStay = useCallback(() => {
    dispatch({ type: ACTION.CLOSE_STAY });
    clearSessionExpiredStorage();
    navigate(ROUTES.HOME, { replace: true });
  }, [clearSessionExpiredStorage, navigate]);

  const onLogin = useCallback(() => {
    dispatch({ type: ACTION.CLOSE_LOGIN });
    clearSessionExpiredStorage();
    navigate(ROUTES.LOGIN, { state: state.loginState, replace: true });
  }, [clearSessionExpiredStorage, navigate, state.loginState]);

  return {
    allowPath: state.allowPath,
    isModalOpen: state.isModalOpen,
    loginState: state.loginState,
    onStay,
    onLogin,
  };
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ACTION.OPEN:
      return { allowPath: action.allowPath, isModalOpen: true, loginState: action.loginState };
    case ACTION.CLOSE_STAY:
      return { ...state, isModalOpen: false };
    case ACTION.CLOSE_LOGIN:
      return { ...state, isModalOpen: false, allowPath: null };
    default:
      return state;
  }
}

function getMatchedServicePath(pathname: string): Nullable<string> {
  return SERVICE_PATH_PREFIXES.find((path) => pathname.startsWith(path)) ?? null;
}
